import { useCallback } from "react";
import { useApolloClient, type DocumentNode } from "@apollo/client";
import type { Classroom, Instructor, Query } from "@models/ISchema";
import { classroomsAvailabilities } from "@services/collections/advance-search/graphql/classroomAdvanceSearch.query";
import { instructorsAdvancedSearch } from "@services/collections/advance-search/graphql/instructorAdvanceSearch.query";

interface ResourcesContext {
  scenarioId: string;
  originId: string;
}

export interface ProposalResourcesById {
  classroomsById: Record<string, Classroom>;
  instructorsById: Record<string, Instructor>;
}

const byId = <T extends { id: string }>(items: (T | null)[] | null | undefined) =>
  Object.fromEntries(
    (items ?? []).filter((item): item is T => !!item).map(item => [String(item.id), item]),
  );

const toNumericIds = (ids: string[]): number[] => ids.map(Number).filter(Number.isFinite);

/**
 * Loads by id the classrooms and instructors a proposal mentions that the link's sessions do not
 * have, with the documents of the editor's advanced search, so they have the shape the form handles.
 * A failure rejects: applying a proposal with half its resources unresolved is worse than not
 * opening it.
 */
export const useProposalResourcesLoader = () => {
  const client = useApolloClient();

  return useCallback(
    async (
      { scenarioId, originId }: ResourcesContext,
      { classroomIds, instructorIds }: { classroomIds: string[]; instructorIds: string[] },
    ): Promise<ProposalResourcesById> => {
      const lookup = (query: DocumentNode, ids: string[]) =>
        ids.length
          ? client.query<Query>({
              query,
              variables: {
                scenarioId,
                originId,
                filter: {
                  fields: { id: { in: toNumericIds(ids) } },
                  pagination: { page: 1, size: ids.length },
                },
              },
              fetchPolicy: "no-cache",
              errorPolicy: "none",
            })
          : Promise.resolve(null);

      const [classrooms, instructors] = await Promise.all([
        lookup(classroomsAvailabilities, classroomIds),
        lookup(instructorsAdvancedSearch, instructorIds),
      ]);

      return {
        classroomsById: byId<Classroom>(classrooms?.data?.cube?.classroomsAvailabilities?.items),
        instructorsById: byId<Instructor>(
          instructors?.data?.cube?.instructorsAdvancedSearch?.items,
        ),
      };
    },
    [client],
  );
};
